import { useState, useEffect, useReducer, useRef } from "react";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import logger from "../../utils/logger";
import { DEFAULT_PROVIDER, PROVIDERS, getWhatsAppProviderLabel } from "../../constants/whatsappProviders";
import { SocketManager } from "../../context/Socket/SocketContext";
import { i18n } from "../../translate/i18n";

// Codes emitted by the Baileys adapter for corrupt local sessions.
// Only the Baileys adapter emits these — this set lives here, not in views.
const BAILEYS_BAD_SESSION_REASONS = new Set([
  "BAD_SESSION",
  "BAD_SESSION_CLEANUP_FAILED",
  "BAD_SESSION_CLEANED",
]);

/**
 * Enriches a raw WhatsApp record with provider-agnostic capability flags.
 * Views must consume these flags instead of checking provider or
 * lastDisconnectReason directly.
 *
 *   canCleanSession  — true only for Baileys connections with a corrupt session
 *   hasQrCapability  — true when the connection is waiting for a QR scan
 *   displayStatus    — normalized status string for rendering
 */
/**
 * Quita del payload los campos de IDENTIDAD que llegan vacíos.
 *
 * Un evento de "cambio de estado" no sabe cómo se llama la conexión; si lo manda en
 * blanco no está diciendo «ahora se llama así», está diciendo «no lo sé». Dejar pasar
 * ese `""` borra el nombre bueno que ya teníamos y la tarjeta cae a su texto por
 * defecto. El estado sí puede venir vacío y significa algo, por eso no está aquí.
 */
const CAMPOS_DE_IDENTIDAD = ["name", "number"];

function sinIdentidadVacia(payload) {
  const limpio = { ...payload };
  CAMPOS_DE_IDENTIDAD.forEach(campo => {
    const valor = limpio[campo];
    if (valor === "" || valor === null || valor === undefined) delete limpio[campo];
  });
  return limpio;
}

function normalizeWhatsApp(wa) {
  const provider = wa.provider ?? DEFAULT_PROVIDER;
  const status   = wa.status ?? "DISCONNECTED";
  const reason   = wa.lastDisconnectReason ?? "";

  return {
    ...wa,
    provider,
    providerLabel: getWhatsAppProviderLabel(provider),
    canCleanSession: provider === PROVIDERS.BAILEYS && BAILEYS_BAD_SESSION_REASONS.has(reason),
    // WABA (Meta Cloud API) no usa QR (no hay socket persistente), pero SÍ tiene
    // ciclo Detener/Reconectar (MT-8, 0366): reconectar = re-validar credenciales
    // + re-suscribir el webhook (idempotente vía restartSpecific → initSession).
    usesQr: ![PROVIDERS.WABA, PROVIDERS.FACEBOOK, PROVIDERS.INSTAGRAM, PROVIDERS.TELEGRAM, PROVIDERS.EMAIL].includes(provider),
    canRestart: true,
    hasQrCapability: provider !== PROVIDERS.WABA && provider !== PROVIDERS.TELEGRAM && provider !== PROVIDERS.EMAIL && (status === "qrcode" || status === "OPENING" || status === "PENDING"),
    // "Detener" (STOP) por canal (MT-8, 0366):
    //   Baileys → mientras NO esté ya detenida manualmente (USER_DISCONNECT); cubre
    //     CONNECTED y los estados de reconexión (donde antes no había cómo cortar el bucle).
    //   WABA → cuando está CONNECTED (Detener = desuscribir el webhook del WABA).
    canStop:
      (provider === PROVIDERS.BAILEYS && reason !== "USER_DISCONNECT") ||
      (provider === PROVIDERS.WABA && status === "CONNECTED") ||
      // Telegram: Detener = retirar el webhook; Reconectar lo vuelve a registrar (0438).
      (provider === PROVIDERS.TELEGRAM && status === "CONNECTED") ||
      // Telegram cuenta personal (0438 D-V18): Detener conserva la sesión; Reconectar la reanuda sin QR.
      (provider === PROVIDERS.TELEGRAM_USER && status === "CONNECTED") ||
      // Correo (0445): Detener cierra la lectura del buzón; Reconectar la vuelve a abrir.
      (provider === PROVIDERS.EMAIL && status === "CONNECTED"),
    // MT-9 (0366): progreso de reconexión (intento N / máx), uniforme a todo canal.
    // Solo presente durante los reintentos; un update normal (connected/disconnected)
    // no los trae → se limpian al reemplazar el objeto en el reducer.
    reconnectAttempt: wa.reconnectAttempt ?? wa.attemptNumber ?? null,
    reconnectMaxAttempts: wa.reconnectMaxAttempts ?? wa.maxAttempts ?? null,
    displayStatus:
      status === "CONNECTED"    ? "connected"  :
      status === "OPENING"      ? "opening"    :
      status === "qrcode"       ? "qrcode"     :
                                  "disconnected",
  };
}

const reducer = (state, action) => {
  if (action.type === "LOAD_WHATSAPPS") {
    return action.payload.map(normalizeWhatsApp);
  }

  if (action.type === "ADD_WHATSAPPS") {
    return [normalizeWhatsApp(action.payload), ...state];
  }

  if (action.type === "UPDATE_WHATSAPPS") {
    const whatsAppIndex = state.findIndex((s) => s.id === action.payload.id);
    if (whatsAppIndex === -1) {
      return [normalizeWhatsApp(action.payload), ...state];
    }
    // Merge, no reemplazo — mismo criterio que RECONNECT_PROGRESS/UPDATE_SESSION
    // más abajo. `SmartUpdateService.emitWhatsappUpdate` (backend) manda payloads
    // PARCIALES a propósito (solo lo que cambió); reemplazar el objeto entero
    // vaciaba cualquier campo que ese evento en particular no trajera —p. ej.
    // `name`, que ningún emisor de "cambio de estado" incluye— aunque el valor
    // real siguiera intacto en la base. El progreso de reconexión SÍ se limpia a
    // propósito: llegar aquí (no a RECONNECT_PROGRESS) significa que terminó.
    // Y fusionar tampoco basta por sí solo: protege de los campos AUSENTES, pero una
    // cadena vacía viaja como valor presente y pisa igual al bueno. `StatusCacheService`
    // emitía `name: ""` cuando su caché —que vive en memoria del proceso— aún no tenía
    // el nombre, y la tarjeta volvía a decir "Conexión principal" con el valor intacto en
    // la base. Se corrigió el emisor; esto impide que el próximo repita la jugada.
    state[whatsAppIndex] = normalizeWhatsApp({
      ...state[whatsAppIndex],
      ...sinIdentidadVacia(action.payload),
      reconnectAttempt: null,
      reconnectMaxAttempts: null,
    });
    return [...state];
  }

  if (action.type === "UPDATE_SESSION") {
    const whatsApp = action.payload;
    const whatsAppIndex = state.findIndex((s) => s.id === whatsApp.id);
    if (whatsAppIndex !== -1) {
      state[whatsAppIndex] = normalizeWhatsApp({
        ...state[whatsAppIndex],
        status:    whatsApp.status,
        updatedAt: whatsApp.updatedAt,
        qrcode:    whatsApp.qrcode,
        // retries omitted — Baileys-specific field, not part of the normalized contract
      });
      return [...state];
    }
    return [...state];
  }

  if (action.type === "RECONNECT_PROGRESS") {
    // MT-9: merge (no reemplazo) para conservar queues/prompt del objeto mientras
    // solo se actualiza el progreso de reintento. Al conectar/desconectar, un
    // UPDATE_WHATSAPPS reemplaza el objeto y limpia el progreso.
    const payload = action.payload;
    const idx = state.findIndex((s) => s.id === payload.id);
    if (idx === -1) return state;
    state[idx] = normalizeWhatsApp({
      ...state[idx],
      status: payload.status || state[idx].status,
      reconnectAttempt: payload.attemptNumber ?? null,
      reconnectMaxAttempts: payload.maxAttempts ?? null,
    });
    return [...state];
  }

  if (action.type === "DELETE_WHATSAPPS") {
    const whatsAppId = action.payload;
    const whatsAppIndex = state.findIndex((s) => s.id === whatsAppId);
    if (whatsAppIndex !== -1) {
      state.splice(whatsAppIndex, 1);
    }
    return [...state];
  }

  if (action.type === "REFRESH_WHATSAPPS") {
    return action.payload.map(normalizeWhatsApp);
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useWhatsApps = () => {
  const [whatsApps, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(true);
  const isMounted = useRef(true);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // ✅ FUNCIÓN PARA REFRESCAR DATOS
  const refreshWhatsApps = async () => {
    if (isMounted.current) {
      try {
        setLoading(true);
        logger.whatsapp.debug("🔄 REFRESCANDO DATOS DE WHATSAPP...");
        const { data } = await api.get("/whatsapp/?session=0");
        if (isMounted.current) {
          dispatch({ type: "REFRESH_WHATSAPPS", payload: data });
          logger.whatsapp.debug("✅ DATOS REFRESCADOS:", data.length, "conexiones");
        }
      } catch (err) {
        if (isMounted.current) {
          logger.whatsapp.error("❌ ERROR AL REFRESCAR:", err);
          toastError(err);
        }
      } finally {
        if (isMounted.current) {
          setLoading(false);
        }
      }
    }
  };

  useEffect(() => {
    // ✅ CORREGIDO: Solo cargar una vez al montar
    if (!isMounted.current) return;
    
    setLoading(true);
    const fetchSession = async () => {
      try {
        logger.whatsapp.debug("🔄 CARGANDO CONEXIONES...");
        const { data } = await api.get("/whatsapp/?session=0");
        if (isMounted.current) {
          dispatch({ type: "LOAD_WHATSAPPS", payload: data });
          logger.whatsapp.debug("✅ CONEXIONES CARGADAS:", data.length);
          setLoading(false);
        }
      } catch (err) {
        if (isMounted.current) {
          logger.whatsapp.error("❌ ERROR AL CARGAR CONEXIONES:", err);
          setLoading(false);
          // ✅ NO mostrar toastError para evitar spam
          console.error("Error cargando WhatsApp:", err);
        }
      }
    };
    fetchSession();
  }, []); // ✅ Solo se ejecuta una vez

  // Listener global: activo en cualquier página porque WhatsAppsProvider está en la raíz.
  // Reacciona a cambios de estado de conexión WhatsApp en tiempo real sin depender
  // de que la página Connections esté montada.
  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    if (!companyId) return;

    const socket = SocketManager.getSocket(companyId);

    // Dedup: evitar toasts repetidos si llegan múltiples eventos en ráfaga
    const lastToastState = {};

    const handleWhatsApp = (data) => {
      if (!isMounted.current || !data) return;
      const { action, whatsapp } = data;
      if (!whatsapp) return;

      if (action === "create") {
        dispatch({ type: "ADD_WHATSAPPS", payload: whatsapp });
      } else if (action === "delete") {
        dispatch({ type: "DELETE_WHATSAPPS", payload: whatsapp.id });
      } else if (action === "reconnecting") {
        // MT-9 (0366): progreso de reintento — merge para no perder el resto del objeto.
        dispatch({ type: "RECONNECT_PROGRESS", payload: whatsapp });
      } else {
        dispatch({ type: "UPDATE_WHATSAPPS", payload: whatsapp });

        const name = whatsapp.name || `#${whatsapp.id}`;
        const toastKey = `${whatsapp.id}-${action}`;

        if (action === "disconnected" && lastToastState[toastKey] !== "shown") {
          lastToastState[toastKey] = "shown";
          setTimeout(() => { delete lastToastState[toastKey]; }, 10000);
          toast.error(i18n.t("connections.toasts.disconnected", { name }), {
            toastId: `wa-disconnected-${whatsapp.id}`,
            autoClose: 6000,
          });
        } else if (action === "connected" && lastToastState[toastKey] !== "shown") {
          lastToastState[toastKey] = "shown";
          setTimeout(() => { delete lastToastState[toastKey]; }, 10000);
          toast.success(i18n.t("connections.toasts.connected", { name }), {
            toastId: `wa-connected-${whatsapp.id}`,
            autoClose: 5000,
          });
        }
      }
    };

    const handleSession = (data) => {
      if (!isMounted.current || !data?.session) return;
      dispatch({ type: "UPDATE_SESSION", payload: data.session });
    };

    // Reconexión socket → re-sincronizar todos los estados (pueden haber cambiado offline)
    const handleReconnect = () => {
      if (isMounted.current) refreshWhatsApps();
    };

    socket.on(`company-${companyId}-whatsapp`, handleWhatsApp);
    socket.on(`company-${companyId}-whatsappSession`, handleSession);
    socket.on("connect", handleReconnect);

    return () => {
      socket.off(`company-${companyId}-whatsapp`, handleWhatsApp);
      socket.off(`company-${companyId}-whatsappSession`, handleSession);
      socket.off("connect", handleReconnect);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return { whatsApps, loading, refreshWhatsApps, dispatch };
};

export default useWhatsApps;
